import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  InputAdornment,
  Chip
} from '@mui/material';
import { 
  Add as AddIcon, 
  Search as SearchIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Visibility as VisibilityIcon 
} from '@mui/icons-material';

import { Department, DepartmentFormData, DepartmentFilterParams } from '../../types/department.types';
import { DepartmentService } from '../../services/department.service';
import { DepartmentForm } from '../../components/DepartmentForm/DepartmentForm';
import PageContainer from '../../../../../components/layout/PageContainer';
import ConfirmationDialog from '../../../../../components/common/ConfirmationDialog';

interface DepartmentListResponse {
  data: Department[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const DepartmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  
  // State for departments data and UI
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [departmentToDelete, setDepartmentToDelete] = useState<Department | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Pagination state
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);

  // Check if we're in edit mode based on URL
  const isEditMode = /\/departments\/[\w-]+\/edit$/.test(location.pathname);
  const isNewMode = location.pathname.endsWith('/new');
  
  // Fetch departments with pagination and filters
  const fetchDepartments = useCallback(async () => {
    try {
      setIsLoading(true);
      const params: DepartmentFilterParams = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery || undefined,
      };
      
      const response = await DepartmentService.getDepartments(params);
      setDepartments(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error('Error fetching departments:', error);
      enqueueSnackbar('Failed to load departments', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, enqueueSnackbar]);
  
  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Fetch departments when filters or pagination changes
  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  // Handle form open/close based on URL
  useEffect(() => {
    if (isNewMode) {
      setSelectedDepartment(null);
      setIsFormOpen(true);
    } else if (isEditMode) {
      // Extract departmentId from path: /departments/:id/edit
      const matches = location.pathname.match(/\/departments\/([\w-]+)\/edit$/);
      const departmentId = matches ? matches[1] : null;
      if (departmentId) {
        handleEditDepartment(departmentId);
      }
    } else {
      setIsFormOpen(false);
    }
  }, [isNewMode, isEditMode, location.pathname]);

  // Handle create/update department
  const handleSubmit = async (data: DepartmentFormData) => {
    try {
      setIsSubmitting(true);
      
      if (selectedDepartment) {
        // Update existing department
        await DepartmentService.updateDepartment(selectedDepartment.id, data);
        enqueueSnackbar('Department updated successfully', { variant: 'success' });
      } else {
        // Create new department
        await DepartmentService.createDepartment(data);
        enqueueSnackbar('Department created successfully', { variant: 'success' });
      }
      
      // Refresh the list and close the form
      await fetchDepartments();
      setIsFormOpen(false);
      navigate('/settings/departments');
    } catch (error) {
      console.error('Error saving department:', error);
      enqueueSnackbar('Failed to save department', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit department
  const handleEditDepartment = async (id: string) => {
    try {
      setIsLoading(true);
      const department = await DepartmentService.getDepartmentById(id);
      setSelectedDepartment(department);
      setIsFormOpen(true);
    } catch (error) {
      console.error('Error fetching department:', error);
      enqueueSnackbar('Failed to load department details', { variant: 'error' });
      navigate('/settings/departments');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = (department: Department) => {
    setDepartmentToDelete(department);
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (!departmentToDelete) return;
    
    try {
      setIsSubmitting(true);
      await DepartmentService.deleteDepartment(departmentToDelete.id);
      enqueueSnackbar('Department deleted successfully', { variant: 'success' });
      await fetchDepartments();
    } catch (error) {
      console.error('Error deleting department:', error);
      enqueueSnackbar('Failed to delete department', { variant: 'error' });
    } finally {
      setDepartmentToDelete(null);
      setIsSubmitting(false);
    }
  };

  // Handle status toggle
  const handleToggleStatus = async (department: Department) => {
    try {
      setIsSubmitting(true);
      const newStatus = department.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      await DepartmentService.updateDepartment(department.id, {
        status: newStatus
      } as Partial<Department>);
      
      enqueueSnackbar(
        `Department ${newStatus === 'INACTIVE' ? 'deactivated' : 'activated'} successfully`,
        { variant: 'success' }
      );
      await fetchDepartments();
    } catch (error) {
      console.error('Error toggling department status:', error);
      enqueueSnackbar('Failed to update department status', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle add new department
  const handleAddNew = () => {
    navigate('/settings/departments/new');
  };

  // Handle close form
  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedDepartment(null);
    navigate('/settings/departments');
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'error';
      case 'MAINTENANCE':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <PageContainer title="Departments">
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <TextField
              placeholder="Search departments..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              disabled={isLoading || isSubmitting}
            >
              Add Department
            </Button>
          </Box>

          {isLoading ? (
            <LinearProgress />
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {departments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No departments found
                        </TableCell>
                      </TableRow>
                    ) : (
                      departments.map((dept) => (
                        <TableRow key={dept.id} hover>
                          <TableCell>{dept.code}</TableCell>
                          <TableCell>{dept.name}</TableCell>
                          <TableCell>{dept.deptType || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={dept.status}
                              color={getStatusColor(dept.status) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatDate(dept.createdAt)}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/settings/departments/${dept.id}`)}
                              color="info"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/settings/departments/${dept.id}/edit`)}
                              color="primary"
                              disabled={isSubmitting}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(dept)}
                              color="error"
                              disabled={isSubmitting}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Department Form Dialog */}
      <Dialog
        open={isFormOpen}
        onClose={handleCloseForm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedDepartment ? 'Edit Department' : 'Add New Department'}
        </DialogTitle>
        <DialogContent>
          <DepartmentForm
            initialData={selectedDepartment || undefined}
            onSubmit={handleSubmit}
            onCancel={handleCloseForm}
            isSubmitting={isSubmitting}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={!!departmentToDelete}
        title="Delete Department"
        message={`Are you sure you want to delete "${departmentToDelete?.name}"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDepartmentToDelete(null)}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </PageContainer>
  );
};

export default DepartmentsPage;
